<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import { eqsApi } from '@/api/eqs';
import FmeaAiPanel from '@/components/FmeaAiPanel.vue';
import FmeaRatingDialog from '@/components/FmeaRatingDialog.vue';
import FmeaTreeNode from '@/components/FmeaTreeNode.vue';
import type { FmeaNode, FmeaTreeResponse, PfmeaImportPreview } from '@/types/domain';
import { notify } from '@/services/notice';
import { errorMessage } from '@/utils/format';
import { confirmAction } from '@/services/confirm';

const props = defineProps<{ projectId: number | string; documentId: number | string }>();
/** 结构树读不出来时给父级的两个出口：关掉这条正文 / 回列表另选（父级清选中，不跳路由） */
const emit = defineEmits<{ (e: 'clear-selection'): void; (e: 'back-to-list'): void }>();

const data = ref<FmeaTreeResponse | null>(null);
const loading = ref(false);
const error = ref('');
/** 接口带回的 HTTP 状态（取得到才有）：文本里没写 404 时也能认出「找不到」 */
const errorStatus = ref<number | null>(null);
const expanded = ref<Set<number>>(new Set());
const showUnrated = ref(false);
const importInput = ref<HTMLInputElement | null>(null);
const importPreview = ref<PfmeaImportPreview | null>(null);
const importLoading = ref(false);
const importConfirming = ref(false);
const importOpen = ref(false);

const KIND_LABEL: Record<string, string> = {
  work_element: '工作要素',
  characteristic: '特殊特性',
  process_step: '过程步骤',
  function: '功能',
  failure: '失效',
  action: '措施',
  unknown: '条目',
};

async function load() {
  loading.value = true;
  error.value = '';
  errorStatus.value = null;
  try {
    data.value = await eqsApi.pfmeaTree(props.projectId, props.documentId);
    // 只展开第一层：1298 条失效全展开没法看
    expanded.value = new Set((data.value?.tree ?? []).map((node) => node.id));
  } catch (caught) {
    data.value = null;
    error.value = errorMessage(caught);
    const status = (caught as { status?: unknown } | null)?.status;
    errorStatus.value = typeof status === 'number' ? status : null;
  } finally {
    loading.value = false;
  }
}

watch(() => [props.projectId, props.documentId], load, { immediate: true });

/*
  「PFMEA 1157 不属于项目 1」/ 404：这条记录挂错了项目，或者已经不在了。
  主文案按这一类说成人话；接口原话收进「技术详情」，排查时照样拿得到。
*/
const isOwnershipError = computed(() =>
  errorStatus.value === 404 || /不属于项目|\b404\b/u.test(error.value));

function openImport() {
  importOpen.value = !importOpen.value;
  importPreview.value = null;
  if (!importOpen.value && importInput.value) importInput.value.value = '';
}

function resetImportPreview() {
  importPreview.value = null;
}

async function previewImport() {
  const file = importInput.value?.files?.[0];
  if (!file) {
    notify('error', '请选择 PFMEA Excel', '支持 .xlsx，单个文件不超过 20MB。');
    return;
  }
  importLoading.value = true;
  try {
    const form = new FormData();
    form.append('file', file);
    importPreview.value = await eqsApi.previewPfmeaImport(props.projectId, props.documentId, form);
  } catch (caught) {
    importPreview.value = null;
    notify('error', 'PFMEA 解析失败', errorMessage(caught));
  } finally {
    importLoading.value = false;
  }
}

async function confirmImport() {
  const preview = importPreview.value;
  if (!preview?.canImport || preview.errors.length) return;
  const title = preview.willReplace
    ? `当前结构有 ${preview.existingTotal} 个条目。确认用 Excel 解析结果整体替换？`
    : `确认导入 ${preview.sourceRows} 行 PFMEA 数据？`;
  if (!await confirmAction({ title, danger: preview.willReplace })) return;
  importConfirming.value = true;
  try {
    const result = await eqsApi.confirmPfmeaImport(
      props.projectId, props.documentId, preview.records, preview.willReplace,
      { fileName: preview.fileName, sheet: preview.sheet },
    );
    data.value = result.tree;
    expanded.value = new Set((result.tree.tree ?? []).map((node) => node.id));
    importPreview.value = null;
    importOpen.value = false;
    if (importInput.value) importInput.value.value = '';
    notify('success', `已导入 ${result.imported} 个结构化条目`, `来源 Excel ${result.sourceRows} 行。`);
  } catch (caught) {
    notify('error', 'PFMEA 导入失败', errorMessage(caught));
  } finally {
    importConfirming.value = false;
  }
}

const saving = ref(false);
/** AI 生成的目标功能：失效链只能挂功能下，所以必须先点一个功能 */
const activeFunction = ref<FmeaNode | null>(null);

/** 找到该功能所属过程步骤的名字与工作要素，作为生成上下文 */
const aiContext = computed(() => {
  const target = activeFunction.value;
  if (!target) return { processName: '', workElements: [] as Array<{ type: string; description: string }> };
  const step = (data.value?.tree ?? []).find((node) =>
    node.children.some((child) => child.id === target.id));
  const workElements = (data.value?.unattached ?? [])
    .filter((node) => node.kind === 'work_element')
    .slice(0, 12)
    .map((node) => ({ type: node.sourceTable === 'pf_work_elements' ? 'method' : 'method', description: node.title }));
  return { processName: step?.title ?? '', workElements };
});

function pickFunction(node: FmeaNode) {
  activeFunction.value = activeFunction.value?.id === node.id ? null : node;
}

function onAdopted(tree: FmeaTreeResponse) {
  const kept = new Set(expanded.value);
  if (activeFunction.value) kept.add(activeFunction.value.id);
  data.value = tree;
  expanded.value = kept;
}

async function mutate(run: () => Promise<FmeaTreeResponse>, ok: string) {
  saving.value = true;
  try {
    const kept = new Set(expanded.value);
    data.value = await run();
    expanded.value = kept;                 // 保持展开状态，别把用户弹回顶层
    notify('success', ok, '');
  } catch (caught) {
    notify('error', '操作失败', errorMessage(caught));
  } finally {
    saving.value = false;
  }
}

/** 失效评分改用弹窗：原来连弹三个 window.prompt，填错没有即时校验、不能返回上一步 */
const ratingNode = ref<FmeaNode | null>(null);

function rate(node: FmeaNode) {
  ratingNode.value = node;
}

async function saveRating(values: {
  severity: number | null; occurrence: number | null; detection: number | null;
}) {
  const node = ratingNode.value;
  if (!node) return;
  await mutate(
    () => eqsApi.updateFmeaItem(props.projectId, props.documentId, node.id, values),
    '评分已保存',
  );
  ratingNode.value = null;
}

const CHILD_OF: Record<string, { kind: string; label: string }> = {
  process_step: { kind: 'function', label: '功能' },
  function: { kind: 'failure', label: '失效' },
  failure: { kind: 'action', label: '措施' },
};

/** 新增条目改用行内表单：原生 prompt 无法校验、无法选失效链类型 */
const draft = ref<{ parent: FmeaNode | null; kind: string; label: string; title: string; failureType: string } | null>(null);

function addChild(node: FmeaNode) {
  const target = CHILD_OF[node.kind];
  if (!target) return;
  draft.value = { parent: node, kind: target.kind, label: target.label, title: '', failureType: 'mode' };
}

function addRoot() {
  draft.value = { parent: null, kind: 'process_step', label: '过程步骤', title: '', failureType: 'mode' };
}

function submitDraft() {
  const d = draft.value;
  if (!d || !d.title.trim()) {
    notify('error', '请填写内容', `${d?.label ?? '条目'}名称不能为空。`);
    return;
  }
  void mutate(
    () => eqsApi.createFmeaItem(props.projectId, props.documentId, {
      kind: d.kind,
      parentId: d.parent?.id ?? null,
      title: d.title.trim(),
      ...(d.kind === 'failure' ? { failureType: d.failureType } : {}),
    }),
    `${d.label}已新增`,
  ).then(() => { draft.value = null; });
}

async function remove(node: FmeaNode) {
  if (node.children.length) {
    notify('error', '无法删除', '该条目下还有子条目，请先删除子条目。');
    return;
  }
  if (!await confirmAction({ title: `确认删除「${node.title}」？`, danger: true })) return;
  void mutate(
    () => eqsApi.deleteFmeaItem(props.projectId, props.documentId, node.id),
    '已删除',
  );
}

const cpGenerating = ref(false);

/** 从当前 PFMEA 生成控制计划（AIAG-VDA 要求 PFMEA 与 CP 同源） */
async function generateControlPlan() {
  if (!data.value?.tree.length) {
    notify('error', '没有过程步骤', 'PFMEA 里还没有过程步骤，无法生成控制计划。');
    return;
  }
  if (!await confirmAction({ title: `将按 ${data.value.tree.length} 道工序生成控制计划（每 3 道一批，可能需要 1-3 分钟）。继续？`, })) return;
  cpGenerating.value = true;
  try {
    const result = await eqsApi.generateControlPlanFromPfmea(props.projectId, props.documentId);
    const lines = result.proposal ?? [];
    if (!lines.length) {
      notify('error', '未生成任何控制计划行', (result.warnings ?? []).join('；') || '请重试');
      return;
    }
    const warnings = result.warnings ?? [];
    const warn = warnings.length ? `\n\n注意：\n${warnings.join('\n')}` : '';
    if (!await confirmAction({ title: `已生成 ${lines.length} 行控制计划，确认写入？${warn}` })) return;
    const adopted = await eqsApi.adoptControlPlan(props.projectId, {
      title: `${data.value.document.title} 控制计划（由 PFMEA 生成）`,
      lines,
    });
    notify('success', `控制计划已生成 ${adopted.adopted} 行`, '可在「控制计划」子页查看与修改。');
  } catch (caught) {
    notify('error', '控制计划生成失败', errorMessage(caught));
  } finally {
    cpGenerating.value = false;
  }
}

function toggle(id: number) {
  const next = new Set(expanded.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  expanded.value = next;
}

function expandAll() {
  const ids = new Set<number>();
  const walk = (node: FmeaNode) => {
    if (node.children.length) ids.add(node.id);
    node.children.forEach(walk);
  };
  (data.value?.tree ?? []).forEach(walk);
  expanded.value = ids;
}

const totals = computed(() => data.value?.totals ?? ({} as Record<string, number>));
const ratedRatio = computed(() => {
  // 按失效模式统计：S/O/D 记在模式行，影响与原因行本就没有分
  const modes = totals.value.failureModes ?? 0;
  return modes ? `${totals.value.ratedModes ?? 0} / ${modes}` : '—';
});
</script>

<template>
  <section class="fmea-tree">
    <header>
      <div>
        <span>PFMEA / STRUCTURE</span>
        <h3>{{ data?.document.title || 'PFMEA 结构树' }}</h3>
      </div>
      <div class="fmea-header-actions">
        <button class="inline-button" type="button" :disabled="importLoading || importConfirming" @click="openImport">
          {{ importOpen ? '收起导入' : 'Excel 智能导入' }}
        </button>
        <button class="inline-button" type="button" :disabled="saving" @click="addRoot">＋ 过程步骤</button>
        <button class="inline-button" type="button" :disabled="cpGenerating" @click="generateControlPlan">
          {{ cpGenerating ? '生成控制计划中…' : '生成控制计划' }}
        </button>
        <button class="inline-button" type="button" :disabled="loading" @click="expandAll">全部展开</button>
        <button class="inline-button" type="button" :disabled="loading" @click="load">
          {{ loading ? '读取中…' : '刷新' }}
        </button>
      </div>
    </header>

    <!--
      读不出来时不再只甩接口原话：先说业务上是怎么回事、能怎么办，原话收进「技术详情」。
      不造假树——没读到就是没读到。
    -->
    <div v-if="error" class="fmea-error-panel" role="alert">
      <p class="fmea-error-title">
        {{ isOwnershipError
          ? '该 PFMEA 不属于当前项目（或记录已不存在），结构树无法显示。'
          : '当前打开的 PFMEA 记录与本项目不匹配或已不可用，结构树无法显示。' }}
      </p>
      <p class="fmea-error-hint">
        请核对该 PFMEA 记录实际所属项目，或到正确项目下重新打开；也可以先重试，或关闭正文、回列表另选一条。
      </p>
      <div class="fmea-error-actions">
        <button class="inline-button" type="button" @click="load">重试</button>
        <button class="inline-button" type="button" @click="emit('clear-selection')">关闭正文</button>
        <button class="inline-button" type="button" @click="emit('back-to-list')">回 PFMEA 列表</button>
      </div>
      <details class="fmea-error-detail">
        <summary>技术详情（排查用）</summary>
        <pre>{{ error }}</pre>
      </details>
    </div>
    <p v-else-if="loading && !data" class="fmea-hint">正在读取结构树…</p>

    <section v-if="importOpen" class="fmea-import">
      <div>
        <b>PFMEA Excel 智能导入</b>
        <p>自动识别多行表头与合并单元格，解析“过程步骤 → 功能 → 失效 → 措施”及 S/O/D。上传后先预览，确认前不会写入。</p>
      </div>
      <div class="fmea-import-file">
        <input ref="importInput" type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" @change="resetImportPreview">
        <button class="button button-secondary" type="button" :disabled="importLoading || importConfirming" @click="previewImport">
          {{ importLoading ? '正在识别…' : '解析并预览' }}
        </button>
      </div>
      <div v-if="importPreview" class="fmea-import-preview">
        <p><b>工作表：{{ importPreview.sheet }}</b> · 源数据 {{ importPreview.sourceRows }} 行 · 结构化后预计
          {{ importPreview.summary.processSteps }} 个过程步骤、{{ importPreview.summary.functions }} 个功能、
          {{ importPreview.summary.failureModes }} 个失效模式、{{ importPreview.summary.actions }} 项措施</p>
        <p class="fmea-import-note">{{ importPreview.mappingNote }}</p>
        <p v-if="importPreview.willReplace" class="fmea-import-danger">
          当前 PFMEA 已有 {{ importPreview.existingTotal }} 个条目；确认导入会整体替换现有结构。
        </p>
        <details>
          <summary>查看识别到的列映射（{{ Object.keys(importPreview.columns).length }} 列）</summary>
          <ul><li v-for="(header, field) in importPreview.columns" :key="field"><b>{{ field }}</b> ← {{ header }}</li></ul>
        </details>
        <ul v-if="importPreview.errors.length" class="fmea-import-errors">
          <li v-for="item in importPreview.errors.slice(0, 12)" :key="`${item.row}-${item.message}`">第 {{ item.row }} 行：{{ item.message }}</li>
        </ul>
        <ul v-if="importPreview.warnings.length" class="fmea-import-warnings">
          <li v-for="item in importPreview.warnings.slice(0, 8)" :key="`${item.row}-${item.message}`">第 {{ item.row }} 行：{{ item.message }}</li>
        </ul>
        <button class="button button-primary" type="button"
          :disabled="!importPreview.canImport || Boolean(importPreview.errors.length) || importConfirming"
          @click="confirmImport">
          {{ importConfirming ? '正在写入…' : importPreview.errors.length ? '请先修正异常行' : importPreview.willReplace ? '确认替换并导入' : '确认导入' }}
        </button>
      </div>
    </section>

    <template v-else-if="data">
      <div class="fmea-totals">
        <span><b>{{ totals.processSteps }}</b>过程步骤</span>
        <span><b>{{ totals.functions }}</b>功能</span>
        <span><b>{{ totals.failures }}</b>失效</span>
        <span><b>{{ totals.actions }}</b>措施</span>
        <span><b>{{ totals.workElements }}</b>工作要素</span>
        <span><b>{{ totals.characteristics }}</b>特殊特性</span>
      </div>

      <div class="fmea-chain">
        <span>
          失效链：影响 {{ totals.failureEffects }} · 模式 {{ totals.failureModes }} · 原因 {{ totals.failureCauses }}
        </span>
        <em>失效模式已评价 S/O/D：{{ ratedRatio }}</em>
        <em v-if="totals.apHigh" class="ap-summary">AP 高 {{ totals.apHigh }} · 中 {{ totals.apMedium }} · 低 {{ totals.apLow }}</em>
        <label><input v-model="showUnrated" type="checkbox"> 只看未评价的失效模式</label>
      </div>

      <FmeaAiPanel
        :project-id="projectId"
        :document-id="documentId"
        :subject="data.document.title"
        :target-function="activeFunction"
        :process-name="aiContext.processName"
        :work-elements="aiContext.workElements"
        @adopted="onAdopted"
      />

      <form v-if="draft" class="fmea-draft" @submit.prevent="submitDraft">
        <b>
          {{ draft.parent ? `在「${draft.parent.title}」下新增${draft.label}` : `新增${draft.label}` }}
        </b>
        <input v-model="draft.title" type="text" :placeholder="`请输入${draft.label}内容`" autofocus>
        <select v-if="draft.kind === 'failure'" v-model="draft.failureType" aria-label="失效链类型">
          <option value="effect">影响 FE</option>
          <option value="mode">模式 FM</option>
          <option value="cause">原因 FC</option>
        </select>
        <button class="button button-primary" type="submit" :disabled="saving">保存</button>
        <button class="button" type="button" :disabled="saving" @click="draft = null">取消</button>
      </form>

      <div class="fmea-nodes">
        <FmeaTreeNode
          v-for="node in data.tree"
          :key="node.id"
          :node="node"
          :expanded="expanded"
          :show-unrated="showUnrated"
          :active-function-id="activeFunction?.id ?? null"
          @toggle="toggle"
          @pick="pickFunction"
          @rate="rate"
          @add="addChild"
          @remove="remove"
        />
      </div>

      <details v-if="data.unattached.length" class="fmea-unattached">
        <summary>
          无父级条目 {{ data.unattached.length }} 项 —— 工作要素与特殊特性在旧库中本就没有挂接关系，未塞进树里
        </summary>
        <ul>
          <li v-for="node in data.unattached" :key="node.id">
            <i>{{ KIND_LABEL[node.kind] || node.kind }}</i>{{ node.title }}
          </li>
        </ul>
      </details>
    </template>

    <FmeaRatingDialog
      :node="ratingNode"
      :saving="saving"
      @close="ratingNode = null"
      @submit="saveRating"
    />
  </section>
</template>

<style scoped>
.fmea-tree { display: grid; gap: 10px; padding: 14px 16px; margin-bottom: 14px; border: 1px solid var(--line); background: var(--white); }
.fmea-tree > header { display: flex; align-items: flex-end; justify-content: space-between; gap: 10px; flex-wrap: wrap; }
.fmea-tree header span { color: var(--muted); font: 700 10px var(--display); letter-spacing: .08em; }
.fmea-tree header h3 { margin: 2px 0 0; font-size: 15px; }
.fmea-header-actions { display: flex; gap: 6px; }
.fmea-error-panel { display: grid; gap: 6px; padding: 10px 12px; border-left: 3px solid #c0392b; background: #fdecea; }
.fmea-error-title { margin: 0; color: #922; font-size: 13px; font-weight: 700; }
.fmea-error-hint { margin: 0; color: var(--ink); font-size: 12px; line-height: 1.55; }
.fmea-error-actions { display: flex; gap: 6px; flex-wrap: wrap; }
.fmea-error-detail { font-size: 12px; color: var(--muted); }
.fmea-error-detail summary { cursor: pointer; }
.fmea-error-detail pre { margin: 4px 0 0; white-space: pre-wrap; word-break: break-all; font: 12px/1.5 monospace; }
.fmea-hint { margin: 0; color: var(--muted); font-size: 12px; }
.fmea-import { display: grid; gap: 10px; padding: 12px; border: 1px solid #9cc4df; background: #f4f9fc; }
.fmea-import p { margin: 3px 0 0; color: var(--muted); font-size: 12px; line-height: 1.55; }
.fmea-import-file { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.fmea-import-file input { flex: 1; min-width: 240px; }
.fmea-import-preview { display: grid; gap: 8px; padding: 10px; border: 1px solid var(--line); background: var(--white); }
.fmea-import-preview > p { margin: 0; color: var(--ink); }
.fmea-import-note { color: var(--blue) !important; }
.fmea-import-danger { padding: 7px 9px; border-left: 3px solid #c0392b; background: #fdecea; color: #922 !important; }
.fmea-import-preview details { font-size: 12px; color: var(--muted); }
.fmea-import-preview ul { margin: 4px 0 0; padding-left: 20px; font-size: 12px; }
.fmea-import-errors { color: #922; }
.fmea-import-warnings { color: #8a5a12; }
.fmea-totals { display: flex; flex-wrap: wrap; gap: 14px; padding: 8px 10px; background: var(--paper); font-size: 12px; color: var(--muted); }
.fmea-totals b { margin-right: 4px; color: var(--ink); font-size: 14px; }
.fmea-chain { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; font-size: 12px; color: var(--muted); }
.fmea-chain em { font-style: normal; color: var(--blue); font-weight: 700; }
.fmea-chain label { display: flex; align-items: center; gap: 4px; cursor: pointer; }
.fmea-draft { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; padding: 10px 12px; border: 1px solid var(--blue); background: rgba(18,109,166,.05); }
.fmea-draft b { font-size: 13px; }
.fmea-draft input { flex: 1; min-width: 220px; height: 34px; padding: 0 9px; border: 1px solid var(--line); background: var(--white); font: inherit; font-size: 13px; }
.fmea-draft select { height: 34px; padding: 0 6px; border: 1px solid var(--line); background: var(--white); font-size: 13px; }
.fmea-nodes { max-height: 460px; overflow-y: auto; border: 1px solid var(--line); }
.fmea-unattached { font-size: 12px; color: var(--muted); }
.fmea-unattached ul { margin: 6px 0 0; padding-left: 16px; max-height: 180px; overflow-y: auto; }
.fmea-unattached i { margin-right: 6px; color: var(--blue); font-style: normal; }

.ap-summary { color: #a35b12 !important; }
</style>

<style>
.fmea-branch { min-width: 0; }
.fmea-row { display: flex; align-items: baseline; gap: 6px; padding: 3px 6px; border-bottom: 1px solid var(--paper); font-size: 12px; }
.fmea-row.unrated { background: #fdf6ec; }
.fmea-toggle { flex: none; width: 16px; height: 16px; padding: 0; border: 1px solid var(--line); background: var(--white); color: var(--blue); font: 700 11px/1 monospace; cursor: pointer; }
.fmea-toggle.placeholder { border: 0; background: none; color: var(--line); cursor: default; text-align: center; }
.fmea-kind { flex: none; padding: 0 4px; border-radius: 3px; background: var(--paper); color: var(--muted); font-size: 12px; font-style: normal; }
.fmea-ft { flex: none; padding: 0 4px; border-radius: 3px; font-size: 12px; font-style: normal; font-weight: 700; }
.ft-effect { background: #fdecea; color: #922; }
.ft-mode { background: #eaf2fd; color: #1c4f91; }
.ft-cause { background: #eef7ec; color: #2b6b32; }
.fmea-title { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.fmea-sod { flex: none; color: var(--muted); font-size: 12px; font-style: normal; }
.fmea-sod.missing { color: #a35b12; }
.fmea-count { flex: none; color: var(--muted); font-size: 12px; }
.fmea-ap { flex: none; padding: 0 5px; border-radius: 3px; font-size: 12px; font-weight: 800; }
.ap-h { background: #fdecea; color: #922; }
.ap-m { background: #fdf3e3; color: #a35b12; }
.ap-l { background: #eef7ec; color: #2b6b32; }
.fmea-ops { flex: none; display: flex; gap: 3px; opacity: 0; transition: opacity .12s; }
.fmea-row:hover .fmea-ops { opacity: 1; }
.fmea-ops button { padding: 0 5px; border: 1px solid var(--line); background: var(--white); color: var(--muted); font-size: 12px; line-height: 16px; cursor: pointer; }
.fmea-row.ai-target { background: #eaf4fd; box-shadow: inset 3px 0 0 var(--blue); }
.fmea-row.ai-target .fmea-ops { opacity: 1; }
.fmea-ops button.active { border-color: var(--blue); background: var(--blue); color: var(--white); }
.fmea-ops button:hover { border-color: var(--blue); color: var(--blue); }
.fmea-children { margin-left: 14px; border-left: 1px dashed var(--line); padding-left: 6px; }
</style>
